import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  TableBody,
  TablePagination,
  IconButton,
  Menu,
} from "@mui/material";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useClientInfo, axiosInstance } from "../../utils/commonImports";
import IndentFormAdd from "./IndentFormAdd";
import { useConfirmation } from "../../hooks/useConfirmation";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import MyDatePicker from "../../components/MyDatePicker";

dayjs.extend(customParseFormat);

const STATUS_FILTERS = ["All", "Pending", "Approved", "Issued", "Closed"];

export default function IndentForm() {
  const { clientId } = useClientInfo();
  const tableRef = useRef();

  // Data states
  const [indents, setIndents] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [bunks, setBunks] = useState([]);

  // Filters states
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterIndentNumber, setFilterIndentNumber] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(null);
  const [filterToDate, setFilterToDate] = useState(null);
  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterVendor, setFilterVendor] = useState("");
  const [filterBunk, setFilterBunk] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Edit/Add states
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);

  // Menu and Snackbar
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [confirm, ConfirmationDialogComponent] = useConfirmation();

  // Fetch dropdowns
  const fetchDropdowns = useCallback(async () => {
    if (!clientId) return;
    try {
      const [vehRes, venRes, bunkRes] = await Promise.all([
        axiosInstance.get("/api/vehicles", { params: { client_id: clientId } }),
        axiosInstance.get("/api/vendors", { params: { client_id: clientId } }),
        axiosInstance.get("/api/bunks", { params: { client_id: clientId } }),
      ]);
      setVehicles(vehRes.data || []);
      setVendors(venRes.data || []);
      setBunks(bunkRes.data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch dropdowns.",
        severity: "error",
      });
    }
  }, [clientId]);

  // Fetch indents
 // Replace your existing fetchIndents with this
const fetchIndents = useCallback(async () => {
  if (!clientId) return;
  try {
    // build params object (axios will encode)
    const params = {
      client_id: clientId,
    };

    if (filterStatus && filterStatus !== "All") params.status = filterStatus;
    if (filterIndentNumber) params.indent_number = filterIndentNumber; // NOTE: 'indent_number' matches backend
    if (filterFromDate)
      params.from_date = dayjs(filterFromDate).format("YYYY-MM-DD");
    if (filterToDate) params.to_date = dayjs(filterToDate).format("YYYY-MM-DD");
    if (filterVehicle) params.vehicle_id = filterVehicle;
    if (filterVendor) params.vendor_id = filterVendor;
    if (filterBunk) params.bunk_id = filterBunk;

    // call the filtered endpoint (backend expects /api/indents/filtered)
    const res = await axiosInstance.get("/api/indents/filtered", { params });

    // backend returns rows array directly (res.data is array)
    setIndents(Array.isArray(res.data) ? res.data : res.data?.rows || []);
  } catch (err) {
    console.error("Filter load error:", err);
    setSnackbar({
      open: true,
      msg: "Failed to fetch fuel indents.",
      severity: "error",
    });
  }
}, [
  clientId,
  filterStatus,
  filterIndentNumber,
  filterFromDate,
  filterToDate,
  filterVehicle,
  filterVendor,
  filterBunk,
]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    fetchIndents();
  }, [fetchIndents]);

  // Pagination handlers
  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Menu handlers
  const handleRowMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleDelete = async (row) => {
    handleRowMenuClose();
    if (!(await confirm("Are you sure you want to delete this indent?")))
      return;
    try {
      await axiosInstance.delete(
        `/api/indents/${row.id}?client_id=${clientId}`
      );

      setSnackbar({
        open: true,
        msg: "Indent deleted successfully",
        severity: "success",
      });
      fetchIndents();
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Delete failed",
        severity: "error",
      });
    }
  };

  const handleEdit = async (row) => {
    try {
      const res = await axiosInstance.get(`/api/indents/${row.id}`, {
        params: { client_id: clientId },
      });
      setEditData(res.data?.data);
      setShowForm(true);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch indent details",
        severity: "error",
      });
    }
    handleRowMenuClose();
  };

  const resetFilters = () => {
    setFilterStatus("All");
    setFilterIndentNumber("");
    setFilterFromDate(null);
    setFilterToDate(null);
    setFilterVehicle("");
    setFilterVendor("");
    setFilterBunk("");
  };

  const FILTER_HEIGHT = 28;
  const FILTER_FONT = "0.7rem";

  const pagedRows = indents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 100px)",
        p: 1,
        bgcolor: "#f8fafc",
      }}
    >
      {/* 🔸 Filter Toolbar */}
      {!showForm && (

<Box
  sx={{
    display: "flex",
    alignItems: "center", // ensures perfect vertical alignment
    gap: 1.2,
    flexWrap: "wrap",
    mb: 1.2,
  }}
>
  {(() => {
    // 🧩 Compact height styling for all filters
    const compactFieldStyle = {
      "& .MuiInputBase-root": { height: 32 }, // 🔹 reduced from 38 → 32
      "& .MuiInputLabel-root": { fontSize: 12.5, top: -4 },
      minWidth: 120,
    };

    return (
      <>
        {/* 🔹 Status Filter */}
        <FormControl size="small" sx={compactFieldStyle}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{
              fontSize: 12.5,
              ".MuiSelect-select": { padding: "2px 6px" },
            }}
          >
            {STATUS_FILTERS.map((st) => (
              <MenuItem key={st} value={st} sx={{ fontSize: 12.5 }}>
                {st}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 🔹 Indent Number */}
        <TextField
          size="small"
          label="Indent No"
          value={filterIndentNumber}
          onChange={(e) => setFilterIndentNumber(e.target.value)}
          sx={compactFieldStyle}
          InputProps={{
            sx: { fontSize: 12.5, height: 32, padding: "0 6px" },
          }}
          InputLabelProps={{ sx: { fontSize: 12.5 } }}
        />

       {/* 🔹 Vehicle Filter */}
<FormControl
  size="small"
  sx={{
    ...compactFieldStyle,
    minWidth: 160, // 🔹 increased width
    width: 160,
  }}
>
  <InputLabel>Vehicle</InputLabel>
 <Select
  value={filterVehicle}
  label="Vehicle"
  onChange={(e) => setFilterVehicle(e.target.value)}
  sx={{
    fontSize: 12.5,
    ".MuiSelect-select": { padding: "4px 8px" },
  }}
>
  <MenuItem value="" sx={{ fontSize: 12.5 }}>
    All
  </MenuItem>

  {vehicles.map((v) => {
    const id =
      v.vehicle_id ||
      v.vehicleid ||
      v.id;

    const name =
      v.vehicle_no ||
      v.vehicle_number ||
      v.vehicleno ||
      v.vehicle ||
      "";

    return (
      <MenuItem key={id} value={id} sx={{ fontSize: 12.5 }}>
        {name}
      </MenuItem>
    );
  })}
</Select>

</FormControl>

{/* 🔹 Vendor Filter */}
<FormControl
  size="small"
  sx={{
    ...compactFieldStyle,
    minWidth: 160, // 🔹 vendors often have longer names
    width: 160,
  }}
>
  <InputLabel>Vendor</InputLabel>
  <Select
    value={filterVendor}
    label="Vendor"
    onChange={(e) => setFilterVendor(e.target.value)}
    sx={{
      fontSize: 12.5,
      ".MuiSelect-select": { padding: "4px 8px" },
    }}
  >
    <MenuItem value="" sx={{ fontSize: 12.5 }}>
      All
    </MenuItem>
    {vendors.map((v) => (
      <MenuItem
        key={v.vendorid}
        value={v.vendorid}
        sx={{ fontSize: 12.5 }}
      >
        {v.vendorname}
      </MenuItem>
    ))}
  </Select>
</FormControl>

{/* 🔹 Bunk Filter */}
<FormControl
  size="small"
  sx={{
    ...compactFieldStyle,
    minWidth: 150, // 🔹 similar to vehicle
    width: 150,
  }}
>
  <InputLabel>Bunk</InputLabel>
  <Select
    value={filterBunk}
    label="Bunk"
    onChange={(e) => setFilterBunk(e.target.value)}
    sx={{
      fontSize: 12.5,
      ".MuiSelect-select": { padding: "4px 8px" },
    }}
  >
    <MenuItem value="" sx={{ fontSize: 12.5 }}>
      All
    </MenuItem>
    {bunks.map((b) => (
      <MenuItem
        key={b.bunkid}
        value={b.bunkid}
        sx={{ fontSize: 12.5 }}
      >
        {b.bunk_name}
      </MenuItem>
    ))}
  </Select>
</FormControl>

    <MyDatePicker
      label="From Date"
      value={filterFromDate}
      onChange={setFilterFromDate}
      inputStyle={{
        fontSize: "0.7rem",
        minHeight: 30,
        height: 30,
        padding: "2px 6px"
      }}
      labelStyle={{
        fontSize: "0.7rem",
        lineHeight: 1,
        marginBottom: 0,
        top: -13, left: 8, // fine tune as needed
      }}
      containerStyle={{
        width: 140,
        minHeight: 30,
        height: 30,
        marginBottom: 0
      }}
    />

    {/* To Date */}
    <MyDatePicker
      label="To Date"
      value={filterToDate}
      onChange={setFilterToDate}
      inputStyle={{
        fontSize: "0.7rem",
        minHeight: 30,
        height: 30,
        padding: "2px 6px"
      }}
      labelStyle={{
        fontSize: "0.7rem",
        lineHeight: 1,
        marginBottom: 0,
        top: -13, left: 8, // fine tune as needed
      }}
      containerStyle={{
        width: 140,
        minHeight: 30,
        height: 30,
        marginBottom: 0
      }}
    />

        {/* 🔹 Buttons */}
        <Button
          variant="outlined"
          size="small"
          onClick={resetFilters}
          sx={{
            height: 32, // 🔹 reduced from 38
            textTransform: "uppercase",
            fontSize: "0.7rem",
            px: 1.2,
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => {
            setEditData(null);
            setShowForm(true);
          }}
          sx={{
            height: 32,
            fontSize: "0.7rem",
            textTransform: "uppercase",
            px: 1.8,
          }}
        >
          Add
        </Button>
      </>
    );
  })()}
</Box>

      )}

      {/* 🔸 Table / Add Form */}
      {showForm ? (
        <IndentFormAdd
          editData={editData}
          onCancel={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            setSnackbar({
              open: true,
              msg: "Indent saved successfully",
              severity: "success",
            });
            fetchIndents();
          }}
        />
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 1250 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 115, // 🔹 fixed width
                    whiteSpace: "nowrap", // 🔹 prevents text wrapping
                  }}
                >
                  Indent No
                </TableCell>

                <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 125, // 🔹 fixed width
                    whiteSpace: "nowrap", // 🔹 prevents text wrapping
                  }}
                >
                  Bunk
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    width: 145, // 🔹 fixed width
                    whiteSpace: "nowrap", // 🔹 prevents text wrapping
                  }}
                >
                  Vehicle / Vendor
                </TableCell>

                {/* Previous KM */}
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Prev KM
                </TableCell>

                {/* Current KM */}
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Curr KM
                </TableCell>

                {/* ✅ Run KM */}
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Run KM
                </TableCell>

                {/* Fuel Qty */}
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Fuel Qty
                </TableCell>

                {/* ✅ Mileage */}
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Mileage (Km/L)
                </TableCell>

                {/* ✅ Tank Balance */}
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Tank Bal (L)
                </TableCell>

                {/* Rate */}
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Rate
                </TableCell>

                {/* Amount */}
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Amount
                </TableCell>

                <TableCell sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.length > 0 ? (
                pagedRows.map((row) => {
                  const runKm =
                    row.current_km_reading && row.previous_km_reading
                      ? Number(row.current_km_reading) -
                        Number(row.previous_km_reading)
                      : 0;

                  return (
                    <TableRow key={row.id} hover>
                      {/* Indent Number */}
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {row.indent_number}
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {dayjs(row.indent_date).format("DD/MM/YYYY")}
                      </TableCell>

                      {/* Bunk */}
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {row.bunk_name}
                      </TableCell>

                      {/* Vehicle or Vendor */}
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {row.vendor_id
                          ? `${row.vendor_name || ""} - ${
                              row.vendor_vehicle_number || ""
                            }`
                          : row.vehicle_number || "-"}
                      </TableCell>

                      {/* Previous KM */}
                      <TableCell
                        sx={{ fontSize: "0.75rem", textAlign: "right" }}
                      >
                        {row.previous_km_reading
                          ? Math.round(Number(row.previous_km_reading))
                          : "-"}
                      </TableCell>

                      {/* Current KM */}
                      <TableCell
                        sx={{ fontSize: "0.75rem", textAlign: "right" }}
                      >
                        {row.current_km_reading
                          ? Math.round(Number(row.current_km_reading))
                          : "-"}
                      </TableCell>

                      {/* ✅ Run KM */}
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          textAlign: "right",
                          fontWeight: "bold",
                          color: "#1976d2",
                        }}
                      >
                        {runKm > 0 ? Math.round(runKm) : 0}
                      </TableCell>

                      {/* Fuel Qty */}
                      <TableCell
                        sx={{ fontSize: "0.75rem", textAlign: "right" }}
                      >
                        {row.fuel_qty
                          ? Number(row.fuel_qty).toFixed(2)
                          : "0.00"}
                      </TableCell>

                      {/* ✅ Mileage */}
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          textAlign: "right",
                          color: "#2e7d32",
                        }}
                      >
                        {row.current_mileage
                          ? Number(row.current_mileage).toFixed(2)
                          : "-"}
                      </TableCell>

                      {/* ✅ Tank Balance */}
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          textAlign: "right",
                          color: "#6a1b9a",
                        }}
                      >
                        {row.tank_balance
                          ? Number(row.tank_balance).toFixed(2)
                          : "0.00"}
                      </TableCell>

                      {/* Rate */}
                      <TableCell
                        sx={{ fontSize: "0.75rem", textAlign: "right" }}
                      >
                        {row.fuel_rate
                          ? Number(row.fuel_rate).toFixed(2)
                          : "0.00"}
                      </TableCell>

                      {/* Amount */}
                      <TableCell
                        sx={{ fontSize: "0.75rem", textAlign: "right" }}
                      >
                        {row.total_amount
                          ? Number(row.total_amount).toFixed(2)
                          : "0.00"}
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ textAlign: "center" }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleRowMenuOpen(e, row)}
                        >
                          <MoreVertIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    No indents found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={indents.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* 🔸 Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRowMenuClose}
      >
        <MenuItem onClick={() => handleEdit(menuRow)} sx={{ fontSize: 13 }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleDelete(menuRow)}
          sx={{ fontSize: 13, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* 🔸 Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%", fontSize: "0.9rem", borderRadius: "8px" }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

      <ConfirmationDialogComponent />
    </Box>
  );
}
